import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pencil, Trash2, Video, Youtube, Link2, Bookmark, Maximize, Minimize } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { parseSource } from "@/lib/embed";
import { deleteMedia } from "@/lib/api";
import { hexToRgb } from "@/lib/registry";
import { saveProgress, getProgress } from "@/lib/watchprogress";
import VideoControls from "@/components/VideoControls";

const SOURCE_META = {
  direct: { Icon: Video, label: "Direct" },
  youtube: { Icon: Youtube, label: "YouTube" },
  vimeo: { Icon: Video, label: "Vimeo" },
  embed: { Icon: Link2, label: "Embed" },
};

const NEXT_UP_DELAY = 10; // seconds countdown before auto-advance

function ActionBtn({ "data-testid": tid, icon: Icon, label, onClick, danger, active, activeColor }) {
  return (
    <button
      type="button"
      data-testid={tid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 h-10 px-3 md:px-4 rounded-full text-sm border transition-colors ${
        danger
          ? "border-white/10 text-white/60 hover:text-[#F43F5E] hover:border-[#F43F5E]/60 hover:bg-[#F43F5E]/10"
          : "border-white/10 text-white/70 hover:text-white hover:border-white/30 hover:bg-white/[0.06]"
      }`}
      style={active && activeColor ? { color: activeColor, borderColor: `${activeColor}60` } : undefined}
      aria-label={label}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/**
 * VideoPlayer — fullscreen in-app overlay.
 *
 * Critical: NO external navigation is permitted. We achieve this with two layers:
 *   1) Technical: the iframe is rendered with `sandbox="allow-scripts allow-same-origin
 *      allow-presentation"`. The absence of `allow-popups` and `allow-top-navigation`
 *      means the browser blocks any attempt by the embedded site (e.g. "Watch on
 *      YouTube") to open a new tab or redirect the parent window.
 *   2) Visual: corner shields cover the spots where YouTube / Vimeo render their
 *      branding & "watch externally" affordances, so they cannot be clicked at all.
 * Right-click context menu is suppressed across the entire overlay to block "Copy
 * video URL" exits as well.
 */
export default function VideoPlayer({
  media,
  profileId,
  accentColor = "#FFFFFF",
  onClose,
  onEdit,
  onDeleted,
  // Playlist context for auto-advance
  playlist,
  playingIdx,
  onAdvance,
  // My List
  myList,
  onToggleList,
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNextUp, setShowNextUp] = useState(false);
  const [nextUpCountdown, setNextUpCountdown] = useState(NEXT_UP_DELAY);

  const rgb = hexToRgb(accentColor);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const source = media ? parseSource(media) : null;
  const meta = source ? (SOURCE_META[source.kind] || SOURCE_META.embed) : SOURCE_META.embed;
  const isIframe = source ? source.kind !== "direct" : false;

  const hasNext = playlist && playingIdx != null && playingIdx < playlist.length - 1;
  const nextItem = hasNext ? playlist[playingIdx + 1] : null;
  const inList = myList && media ? myList.includes(media.id) : false;

  // Reset state when a different media item opens
  useEffect(() => {
    setVideoError(false);
    setVideoLoading(true);
    setVideoKey((k) => k + 1);
    setConfirmingDelete(false);
    setShowNextUp(false);
    setNextUpCountdown(NEXT_UP_DELAY);
    clearInterval(countdownRef.current);
  }, [media?.id]);

  // Restore watch position for direct video
  useEffect(() => {
    if (!media || isIframe) return;
    const saved = getProgress(profileId, media.id);
    if (saved?.seconds > 5) {
      const trySeek = () => {
        const vid = videoRef.current;
        if (vid && vid.duration) {
          vid.currentTime = saved.seconds;
        }
      };
      // Try on loadedmetadata via the onLoadedMetadata handler below, but also retry once
      const t = setTimeout(trySeek, 500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media?.id, isIframe, profileId]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!media) return undefined;
    const onKey = (e) => {
      // Don't fire shortcuts while typing in inputs or while delete dialog is open
      if (confirmingDelete) {
        if (e.key === "Escape") setConfirmingDelete(false);
        return;
      }
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      const vid = videoRef.current;
      switch (e.key) {
        case "Escape":
          if (showNextUp) { setShowNextUp(false); clearInterval(countdownRef.current); }
          else onCloseRef.current?.();
          break;
        case " ":
        case "k":
          if (vid) { e.preventDefault(); vid.paused ? vid.play() : vid.pause(); }
          break;
        case "ArrowLeft":
          if (vid) { e.preventDefault(); vid.currentTime = Math.max(0, vid.currentTime - 10); }
          break;
        case "ArrowRight":
          if (vid) { e.preventDefault(); vid.currentTime = Math.min(vid.duration || 0, vid.currentTime + 10); }
          break;
        case "m":
        case "M":
          if (vid) vid.muted = !vid.muted;
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, confirmingDelete, showNextUp]);

  // Lock to landscape when the player opens; unlock when it closes
  useEffect(() => {
    if (!media) return;
    screen.orientation?.lock?.("landscape").catch(() => {});
    return () => screen.orientation?.unlock?.();
  }, [media?.id]);

  // Track fullscreen changes (user pressing F11 or native fullscreen button)
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    const onWebkitFsChange = () => setIsFullscreen(!!document.webkitFullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onWebkitFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onWebkitFsChange);
    };
  }, []);

  useEffect(() => () => clearInterval(countdownRef.current), []);

  const toggleFullscreen = useCallback(() => {
    // iOS Safari: only <video> can go fullscreen via webkitEnterFullscreen
    if (!isIframe && videoRef.current?.webkitEnterFullscreen && !document.fullscreenElement && !document.webkitFullscreenElement) {
      videoRef.current.webkitEnterFullscreen();
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const req = el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.();
      req?.then?.(() => screen.orientation?.lock?.("landscape").catch(() => {})).catch(() => {});
    } else {
      (document.exitFullscreen?.() ?? document.webkitExitFullscreen?.())?.catch?.(() => {});
    }
  }, [isIframe]);

  // Save watch progress periodically
  const handleTimeUpdate = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !media) return;
    saveProgress(profileId, media.id, vid.currentTime, vid.duration);
  }, [profileId, media]);

  // Restore position when metadata loads
  const handleLoadedMetadata = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !media) return;
    const saved = getProgress(profileId, media.id);
    if (saved?.seconds > 5 && saved.seconds < vid.duration - 10) {
      vid.currentTime = saved.seconds;
    }
  }, [profileId, media]);

  // Auto-advance: show "Next Up" overlay when video ends
  const handleEnded = useCallback(() => {
    if (!hasNext) return;
    setShowNextUp(true);
    setNextUpCountdown(NEXT_UP_DELAY);
    countdownRef.current = setInterval(() => {
      setNextUpCountdown((n) => {
        if (n <= 1) {
          clearInterval(countdownRef.current);
          onAdvanceRef.current?.(nextItem);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  }, [hasNext, nextItem]);

  const handleAdvanceNow = useCallback(() => {
    clearInterval(countdownRef.current);
    setShowNextUp(false);
    onAdvanceRef.current?.(nextItem);
  }, [nextItem]);

  const handleCancelAdvance = useCallback(() => {
    clearInterval(countdownRef.current);
    setShowNextUp(false);
    onCloseRef.current?.();
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteMedia(profileId, media.id);
      toast.success("Deleted");
      setConfirmingDelete(false);
      onDeleted?.();
      onClose?.();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleVideoError = () => {
    setVideoError(true);
    setVideoLoading(false);
    toast.error("Failed to load video");
  };

  const handleRetry = () => {
    setVideoError(false);
    setVideoLoading(true);
    setVideoKey((k) => k + 1);
  };

  return (
    <AnimatePresence>
      {media && source && (
        <motion.div
          key="player-overlay"
          data-testid="video-player-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          onContextMenu={(e) => e.preventDefault()}
          style={{ "--p-color": accentColor, "--p-rgb": rgb }}
          className="fixed inset-0 z-40 bg-[#050507]/97 backdrop-blur-xl flex flex-col safe-area-override"
        >
          {/* Top bar */}
          <header className="flex items-center justify-between gap-4 px-4 md:px-8 py-4 border-b border-white/[0.05]">
            <div className="min-w-0 flex items-center gap-3">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-[10px] uppercase tracking-[0.2em] text-white/70 shrink-0"
                style={{ color: accentColor }}
                data-testid="player-source-badge"
              >
                <meta.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                <span>{meta.label}</span>
              </span>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 truncate">
                  {media.sectionLabel}
                  {playlist && playingIdx != null && ` · ${playingIdx + 1} / ${playlist.length}`}
                </p>
                <h2
                  data-testid="player-title"
                  className="text-base md:text-lg font-medium text-white truncate"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {media.title}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {onToggleList && (
                <ActionBtn
                  data-testid="player-list-btn"
                  icon={Bookmark}
                  label={inList ? "Saved" : "Save"}
                  onClick={() => onToggleList(media.id)}
                  active={inList}
                  activeColor={accentColor}
                />
              )}
              {onEdit && (
                <ActionBtn
                  data-testid="player-edit-btn"
                  icon={Pencil}
                  label="Edit"
                  onClick={() => onEdit(media)}
                />
              )}
              {onDeleted && (
                <ActionBtn
                  data-testid="player-delete-btn"
                  icon={Trash2}
                  label="Delete"
                  danger
                  onClick={() => setConfirmingDelete(true)}
                />
              )}
              {/* Fullscreen toggle */}
              <button
                type="button"
                onClick={toggleFullscreen}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 hover:bg-white/[0.06] transition-colors"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen
                  ? <Minimize className="w-4 h-4" strokeWidth={1.75} />
                  : <Maximize className="w-4 h-4" strokeWidth={1.75} />
                }
              </button>
              <button
                type="button"
                data-testid="player-close-btn"
                onClick={onClose}
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 hover:bg-white/[0.06] transition-colors"
                aria-label="Close player"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </header>

          {/* Player surface */}
          <div className="flex-1 flex items-center justify-center px-3 md:px-8 py-6 md:py-8">
            <motion.div
              ref={containerRef}
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-[0_30px_90px_-30px_rgba(0,0,0,0.8)] border border-white/[0.05]"
              style={{
                boxShadow: `0 30px 90px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(var(--p-rgb),0.12), 0 0 60px -20px rgba(var(--p-rgb),0.45)`,
              }}
            >
              {isIframe ? (
                <iframe
                  data-testid="player-iframe"
                  src={source.src}
                  title={media.title}
                  /* Sandbox: omit allow-popups & allow-top-navigation → no exits */
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  referrerPolicy="strict-origin"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  className="w-full h-full border-0 bg-black"
                />
              ) : (
                <>
                  <video
                    key={videoKey}
                    ref={videoRef}
                    data-testid="player-video"
                    src={source.src}
                    poster={media.posterUrl || undefined}
                    autoPlay
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={handleLoadedMetadata}
                    onCanPlay={() => setVideoLoading(false)}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    onError={handleVideoError}
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full h-full object-contain bg-black"
                  >
                    Your browser does not support the video tag.
                  </video>

                  {/* Loading spinner */}
                  {videoLoading && !videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black pointer-events-none">
                      <div
                        className="w-10 h-10 rounded-full border-2 border-white/20 animate-spin"
                        style={{ borderTopColor: accentColor }}
                      />
                    </div>
                  )}

                  {/* Custom video controls (hidden while loading or error) */}
                  {!videoLoading && !videoError && (
                    <VideoControls
                      videoRef={videoRef}
                      accentColor={accentColor}
                      rgb={rgb}
                      isFullscreen={isFullscreen}
                      onToggleFullscreen={toggleFullscreen}
                    />
                  )}

                  {/* Error state */}
                  {videoError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 gap-3">
                      <p className="text-white/50 text-sm">Failed to load video</p>
                      <button
                        type="button"
                        onClick={handleRetry}
                        className="text-xs text-white/40 hover:text-white underline transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {/* Next Up countdown overlay */}
                  <AnimatePresence>
                    {showNextUp && nextItem && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-end justify-end p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent"
                      >
                        <div
                          className="flex flex-col gap-3 max-w-xs w-full rounded-2xl p-4 border border-white/10"
                          style={{ background: "rgba(10,10,15,0.92)", backdropFilter: "blur(16px)" }}
                        >
                          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                            Up Next in {nextUpCountdown}s
                          </p>
                          {nextItem.posterUrl && (
                            <div
                              className="w-full rounded-lg overflow-hidden aspect-video"
                              style={{ backgroundImage: `url('${nextItem.posterUrl}')`, backgroundSize: "cover", backgroundPosition: "center" }}
                            />
                          )}
                          <p
                            className="text-sm font-semibold text-white leading-snug"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                          >
                            {nextItem.title}
                          </p>
                          {/* Countdown progress bar */}
                          <div className="h-0.5 rounded-full bg-white/15 overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ background: accentColor }}
                              initial={{ width: "100%" }}
                              animate={{ width: "0%" }}
                              transition={{ duration: NEXT_UP_DELAY, ease: "linear" }}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleAdvanceNow}
                              className="flex-1 py-2 rounded-xl text-xs font-bold text-white transition-opacity hover:opacity-80"
                              style={{ background: accentColor, fontFamily: "Outfit, sans-serif", letterSpacing: "0.05em" }}
                            >
                              Play Now
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelAdvance}
                              className="flex-1 py-2 rounded-xl text-xs font-bold text-white/60 border border-white/10 hover:text-white hover:border-white/30 transition-colors"
                              style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              {/* Click-shields over branding / "watch externally" hotspots.
                  These intercept pointer events on top of the iframe so users
                  cannot reach YouTube/Vimeo's external-link affordances. */}
              {isIframe && (
                <>
                  <div
                    data-testid="player-shield-top"
                    className="absolute top-0 left-0 right-0 h-12 md:h-14 pointer-events-auto"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-hidden
                  />
                  <div
                    data-testid="player-shield-bottom-right"
                    className="absolute bottom-0 right-0 w-28 h-10 md:w-36 md:h-12 pointer-events-auto"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-hidden
                  />
                </>
              )}
            </motion.div>
          </div>

          {/* Description footer */}
          {media.description ? (
            <footer className="px-4 md:px-8 pb-5 md:pb-8">
              <p
                data-testid="player-description"
                className="max-w-3xl mx-auto text-sm text-white/55 leading-relaxed text-center whitespace-pre-wrap"
              >
                {media.description}
              </p>
            </footer>
          ) : (
            <div className="h-4 md:h-6" />
          )}

          <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
            <AlertDialogContent
              data-testid="player-delete-confirm-dialog"
              className="bg-[#0a0a0d] border-white/10 text-white"
            >
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this media item?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/50">
                  &ldquo;{media.title}&rdquo; will be permanently removed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid="player-delete-cancel"
                  className="bg-transparent border-white/15 text-white hover:bg-white/[0.05] hover:text-white"
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="player-delete-confirm"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="bg-[#E11D48] hover:bg-[#be123c] text-white"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
